import { create } from 'zustand';
import {
  type ChatEntry,
  type Phase,
  type PlayerId,
  type PrivateState,
  type RoomSnapshot,
} from '../../../shared/types';

interface Seat {
  roomCode: string;
  playerId: PlayerId;
  identity: string;
}

interface GameStore {
  connected: boolean;
  error: string | null;
  roomCode: string | null;
  myPlayerId: PlayerId | null;
  identity: string | null;
  myName: string | null;
  phase: Phase;
  snapshot: RoomSnapshot | null;
  privateState: PrivateState | null;
  chat: ChatEntry[];
  pendingJoinCode: string | null;

  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setPendingJoinCode: (code: string | null) => void;
  setConnectionInfo: (info: Seat) => void;
  restoreIdentityForRoom: (code: string) => boolean;
  clearIdentityForRoom: (code: string) => void;
  setSnapshot: (snapshot: RoomSnapshot) => void;
  setPrivateState: (state: PrivateState) => void;
  pushChat: (entry: ChatEntry) => void;
  reset: () => void;
}

const SESSION_KEY = 'snake-ladder-session';

function identityKey(code: string): string {
  return `snake-ladder-identity-${code.toUpperCase()}`;
}

function loadSession(): Seat | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<Seat>;
    if (s.roomCode && s.playerId && s.identity) {
      return { roomCode: s.roomCode, playerId: s.playerId, identity: s.identity };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeSession(seat: Seat): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(seat));
  } catch {
    /* ignore */
  }
}

function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function writeIdentity(seat: Seat): void {
  try {
    localStorage.setItem(
      identityKey(seat.roomCode),
      JSON.stringify({ playerId: seat.playerId, identity: seat.identity }),
    );
  } catch {
    /* ignore */
  }
}

const stored = loadSession();

const initial = {
  connected: false,
  error: null as string | null,
  roomCode: null as string | null,
  myPlayerId: null as PlayerId | null,
  identity: null as string | null,
  myName: null as string | null,
  phase: 'lobby' as Phase,
  snapshot: null as RoomSnapshot | null,
  privateState: null as PrivateState | null,
  chat: [] as ChatEntry[],
  pendingJoinCode: null as string | null,
};

export const useGameStore = create<GameStore>((set) => ({
  ...initial,
  ...(stored
    ? { roomCode: stored.roomCode, myPlayerId: stored.playerId, identity: stored.identity }
    : {}),

  setConnected: (connected) => set({ connected }),
  setError: (error) => set({ error }),
  setPendingJoinCode: (code) => set({ pendingJoinCode: code }),
  setConnectionInfo: ({ roomCode, playerId, identity }) => {
    const seat = { roomCode, playerId, identity };
    writeSession(seat);
    writeIdentity(seat);
    set({ roomCode, myPlayerId: playerId, identity, pendingJoinCode: null });
  },
  restoreIdentityForRoom: (code) => {
    try {
      const raw = localStorage.getItem(identityKey(code));
      if (!raw) return false;
      const s = JSON.parse(raw) as { playerId?: string; identity?: string };
      if (!s.playerId || !s.identity) return false;
      const seat: Seat = { roomCode: code.toUpperCase(), playerId: s.playerId, identity: s.identity };
      writeSession(seat);
      set({
        roomCode: seat.roomCode,
        myPlayerId: seat.playerId,
        identity: seat.identity,
        pendingJoinCode: null,
      });
      return true;
    } catch {
      return false;
    }
  },
  clearIdentityForRoom: (code) => {
    try {
      localStorage.removeItem(identityKey(code));
    } catch {
      /* ignore */
    }
  },
  setSnapshot: (snapshot) =>
    set({
      snapshot,
      phase: snapshot.phase,
      chat: snapshot.chat,
    }),
  setPrivateState: (state) => set({ privateState: state }),
  pushChat: (entry) =>
    set((s) => {
      const chat = [...s.chat, entry];
      if (chat.length > 200) chat.shift();
      return {
        chat,
        snapshot: s.snapshot ? { ...s.snapshot, chat } : s.snapshot,
      };
    }),
  reset: () => {
    clearSession();
    set({ ...initial });
  },
}));

/** The player record for "me", or null before joining a room. */
export const useMe = () =>
  useGameStore((s) => {
    if (!s.snapshot || !s.myPlayerId) return null;
    return s.snapshot.players.find((p) => p.id === s.myPlayerId) ?? null;
  });

export const useMyColor = () => useMe()?.color ?? null;

export const useRoomCode = () => useGameStore((s) => s.roomCode);
export const usePhase = () => useGameStore((s) => s.phase);
export const useSnapshot = () => useGameStore((s) => s.snapshot);
export const usePrivateState = () => useGameStore((s) => s.privateState);
